/**
 * AI 引用弹出框管理器
 * 用于管理鼠标悬停在 .ai-au 元素上时显示对应的 .ai-news 盒子，移出时隐藏
 */

// 修改全局存储，使用 Map 来存储每个消息的引用数据
const messagesCitationsData = new Map()
let currentPopover = null
let isMouseInPopover = false
let currentHoverButton = null
let leaveTimer = null
const isPopoverVisible = false
const currentCitation = null
// const pendingShowRequest = null
// const transitionTimer = null
// const isPopoverTransitioning = false
let checkPositionTimer = null

// 添加全局变量来存储鼠标位置
let mouseX = 0
let mouseY = 0

// 在文件开头添加鼠标移动事件监听
document.addEventListener('mousemove', (e) => {
  mouseX = e.clientX
  mouseY = e.clientY
})

/**
 * 设置引用数据
 * @param {Object} data - 引用数据对象
 * @param {string} messageId - 消息ID
 */
export function setCitationsData(data, messageId) {
  if (!messageId) return
  messagesCitationsData.set(messageId, data || {})
}

/**
 * 获取引用数据
 * @param {string} citation - 引用编号
 * @param {string} messageId - 消息ID
 */
function getCitationData(citation, messageId) {
  const messageData = messagesCitationsData.get(messageId)
  return messageData ? messageData[citation] : null
}

/**
 * 初始化引用弹出框功能
 */
export function initCitationPopovers(container, data, messageId) {
  if (!container || !messageId) return

  // 存储该消息的引用数据
  setCitationsData(data, messageId)

  // 创建共享弹出框（如果不存在）
  if (!currentPopover) {
    currentPopover = createPopover()
  }

  // 查找所有引用按钮
  const citationButtons = container.querySelectorAll('.ai-au')

  // 为每个引用按钮添加事件监听
  citationButtons.forEach(button => {
    // 移除可能存在的旧事件监听器
    button.removeEventListener('mouseenter', handleCitationMouseEnter)
    button.removeEventListener('mouseleave', handleCitationMouseLeave)

    // 添加新的事件监听器
    button.addEventListener('mouseenter', handleCitationMouseEnter)
    button.addEventListener('mouseleave', handleCitationMouseLeave)
  })
}

/**
 * 处理引用按钮的鼠标进入事件
 */
function handleCitationMouseEnter(event) {
  clearHideTimer()

  const button = event.currentTarget
  const citation = button.dataset.citation
  const messageId = button.dataset.messageId

  if (!citation || !messageId) return

  currentHoverButton = button

  const citationData = getCitationData(citation, messageId)
  if (!citationData) return

  if (!currentPopover) {
    currentPopover = createPopover()
  }

  // 更新内容
  updatePopoverContent(currentPopover, citationData)

  // 直接显示
  currentPopover.style.display = 'block'

  // 计算并设置位置
  updatePopoverPosition(currentPopover, button)

  // 启动位置检查
  startPositionCheck()
}

/**
 * 处理引用按钮的鼠标离开事件
 */
function handleCitationMouseLeave() {
  currentHoverButton = null
  scheduleHideIfNeeded()
}

/**
 * 创建弹出框
 */
function createPopover() {
  const popover = document.createElement('div')
  popover.className = 'ai-news'

  // 移除 transition 相关样式
  popover.style.cssText = `
    position: fixed;
    z-index: 6666;
    display: none;
    background: #FFF;
    border-radius: 10px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
    padding: 20px;
    max-width: 423px;
    width: 423px;
    font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  `

  // 为弹出框添加鼠标事件
  popover.addEventListener('mouseenter', () => {
    isMouseInPopover = true
    clearHideTimer() // 清除任何隐藏定时器
  })

  popover.addEventListener('mouseleave', () => {
    isMouseInPopover = false
    scheduleHideIfNeeded() // 安排隐藏（如果需要）
  })

  document.body.appendChild(popover)
  return popover
}

/**
 * 更新弹出框内容
 */
function updatePopoverContent(popover, data) {
  if (!data) return

  /**
   *  <div class='item-tit' style="display: flex; align-items: center; justify-content: space-between; gap: 6px;">
        <div class='item-tit-lf' style="display: flex; align-items: center; gap: 6px;">
          <img src='${data.sourceIcon || ''}' alt='' style="height: 20px; width: 20px;" />
          <span class='sp-tit' style="color: #000000; font-size: 14px; font-weight: 500;">${data.source || ''}</span>
          <span class='sp-time' style="color: #777777; font-size: 12px; font-weight: 400;">${data.publishTime || ''}</span>
        </div>
        <div></div>
      </div>
   */
  // 使用正确的字段名映射
  popover.innerHTML = `
    <div class='news-item' style="cursor: pointer;">
      <div class='item-new-tit' style="padding-bottom: 17px;padding-top: 10px; line-height: 22px; color: #000000; font-family: 'PingFang SC'; font-size: 14px; font-weight: 600; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; text-overflow: ellipsis;">
        ${data.title || ''}
      </div>
      <div class='item-new-dec' style="color: #777777; font-family: 'PingFang SC'; font-size: 14px; font-weight: 500; line-height: 20px; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden; text-overflow: ellipsis;">
        ${data.content || ''}
      </div>
    </div>
  `

  // 如果有URL，添加点击事件
  if (data.url) {
    const newsItem = popover.querySelector('.news-item')
    if (newsItem) {
      newsItem.addEventListener('click', () => {
        window.open(data.url, '_blank')
      })
    }
  }
}

/**
 * 更新弹出框位置
 */
function updatePopoverPosition(popover, button) {
  const rect = button.getBoundingClientRect()
  const popoverHeight = popover.offsetHeight
  const popoverWidth = popover.offsetWidth

  // 默认显示在按钮上方 6px 处
  let top = rect.top - popoverHeight - 6

  // 如果上方空间不足，则显示在按钮下方 6px 处
  if (top < 10) {
    top = rect.bottom + 6
  }

  // 计算水平居中位置
  let left = rect.left + (rect.width / 2) - (popoverWidth / 2)

  // 确保不超出视口左右边界
  left = Math.max(10, Math.min(left, window.innerWidth - popoverWidth - 10))

  // 设置弹出框位置
  popover.style.top = `${top}px`
  popover.style.left = `${left}px`
}

/**
 * 显示弹出框
 */
// function showPopover(popover) {
//   if (!popover) return
//   popover.style.display = 'block'
// }

/**
 * 隐藏弹出框
 */
function hidePopover(popover) {
  if (!popover) return
  popover.style.display = 'none'
}

/**
 * 安排隐藏弹出框
 */
function scheduleHideIfNeeded() {
  if (leaveTimer) return

  leaveTimer = setTimeout(() => {
    leaveTimer = null
    if (!isMouseInPopover && !currentHoverButton && currentPopover) {
      hidePopover(currentPopover)
    }
  }, 50) // 改为 50ms，原来是 200ms
}

/**
 * 清除隐藏定时器
 */
function clearHideTimer() {
  if (leaveTimer) {
    clearTimeout(leaveTimer)
    leaveTimer = null
  }
}

/**
 * 销毁引用弹出框功能
 */
export function destroyCitationPopovers() {
  // 清除定时器
  clearHideTimer()
  if (checkPositionTimer) {
    clearInterval(checkPositionTimer)
    checkPositionTimer = null
  }

  // 移除弹出框
  if (currentPopover && currentPopover.parentNode) {
    currentPopover.parentNode.removeChild(currentPopover)
    currentPopover = null
  }

  // 重置状态
  isMouseInPopover = false
  currentHoverButton = null
}

/**
 * 更新单个引用的数据
 * @param {string} citation - 引用编号
 * @param {Object} data - 该引用的数据
 */
export function updateCitationData(citation, data) {
  if (!citation) return

  // 更新引用数据
  messagesCitationsData.forEach((messageData, messageId) => {
    messageData[citation] = data
  })

  // 如果当前显示的是该引用，更新弹出框内容
  if (isPopoverVisible && currentCitation === citation && currentPopover) {
    updatePopoverContent(currentPopover, data)
  }
}

/**
 * 获取所有引用数据
 * @returns {Object} - 引用数据对象
 */
export function getCitationsData() {
  const citationsData = {}
  messagesCitationsData.forEach((messageData, messageId) => {
    Object.assign(citationsData, messageData)
  })
  return citationsData
}

// 修改位置检查函数
function startPositionCheck() {
  // 清除可能存在的旧定时器
  if (checkPositionTimer) {
    clearInterval(checkPositionTimer)
  }

  // 每 100ms 检查一次鼠标位置
  checkPositionTimer = setInterval(() => {
    if (!currentHoverButton || !currentPopover) {
      clearInterval(checkPositionTimer)
      checkPositionTimer = null
      return
    }

    // 获取当前按钮的位置信息
    const rect = currentHoverButton.getBoundingClientRect()

    // 检查鼠标是否在按钮区域内
    const isMouseOverButton = (
      mouseX >= rect.left &&
      mouseX <= rect.right &&
      mouseY >= rect.top &&
      mouseY <= rect.bottom
    )

    // 获取弹窗位置信息
    const popoverRect = currentPopover.getBoundingClientRect()

    // 检查鼠标是否在弹窗区域内
    const isMouseOverPopover = (
      mouseX >= popoverRect.left &&
      mouseX <= popoverRect.right &&
      mouseY >= popoverRect.top &&
      mouseY <= popoverRect.bottom
    )

    // 如果鼠标既不在按钮上也不在弹窗内，则隐藏弹窗
    if (!isMouseOverButton && !isMouseOverPopover) {
      hidePopover(currentPopover)
      clearInterval(checkPositionTimer)
      checkPositionTimer = null
      currentHoverButton = null
      isMouseInPopover = false
    }
  }, 100)
}
